import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import {
  InfoModal,
  HorizontalMenuTabs,
  ActionTabs,
  AppIcon,
  REMOTE_PERMISSIONS,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { SOLUTION_SESSION_IDS } from '@backrs_ui/grow-ui/src/models/models';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import {
  selectIsSharedSessionActive,
  selectSharedSessionOwnerFullName,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { useGetResumeTemplateQuery } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.api';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Typography, Button, SxProps, Theme, useMediaQuery } from '@mui/material';

import { ResumeSection, Resume } from '../output_formatters/ResumeBuilder/ResumeSection';
import { copyResume } from '../output_formatters/ResumeBuilder/utils/copyResume';
import {
  createFullResume,
  ResumeContent,
} from '../output_formatters/ResumeBuilder/utils/createResumeSections';
import { downloadResume } from '../output_formatters/ResumeBuilder/utils/downloadResume';

const RESUME_BUILDER_STRINGS = appStrings.modules.solutionSessions.resumeBuilderSolutionBox;

export const ResumeBuilderSolutionBox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams<{ uuid?: string }>();
  const isTablet = useMediaQuery('(max-width: 900px)');
  const activeSession = useAppSelector(getActiveLubavSession);
  const isSharedSession = useAppSelector(selectIsSharedSessionActive);
  const sharedOwnerFullName = useAppSelector(selectSharedSessionOwnerFullName);

  const betaMode = useHasPermission(REMOTE_PERMISSIONS.LUBAV_BETA);
  const user = useAppSelector(selectUser);

  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: isSharedSession,
  });

  const { data: educationTemplate } = useGetResumeTemplateQuery('education');
  const { data: workExperienceTemplate } = useGetResumeTemplateQuery('work_experience');
  const { data: leadershipTemplate } = useGetResumeTemplateQuery('leadership');
  const { data: skillsTemplate } = useGetResumeTemplateQuery('skills');

  const { boxData: educationContent } = useAppSelector(
    (state) => getSessionState(state, SOLUTION_SESSION_IDS.RESUME_BUILDER_EDUCATION).boxData,
  );

  const { boxData: workExperienceContent } = useAppSelector(
    (state) => getSessionState(state, SOLUTION_SESSION_IDS.RESUME_BUILDER_WORK_EXPERIENCE).boxData,
  );

  const { boxData: leadershipContent } = useAppSelector(
    (state) => getSessionState(state, SOLUTION_SESSION_IDS.RESUME_BUILDER_LEADERSHIP).boxData,
  );

  const { boxData: skillsContent } = useAppSelector(
    (state) => getSessionState(state, SOLUTION_SESSION_IDS.RESUME_BUILDER_SKILLS).boxData,
  );

  const { boxData, loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const scrollRef = useRef(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);

  const getResumeContent = (): ResumeContent => {
    const publicProfile = profileData?.profile?.publicProfile;
    const name = publicProfile
      ? `${publicProfile.first_name || ''} ${publicProfile.last_name || ''}`.trim()
      : (sharedOwnerFullName ?? '');

    return {
      name,
      city: publicProfile?.city || undefined,
      state: publicProfile?.state || undefined,
      phone: !isSharedSession ? user?.phone : '(xxx) xxx-xxxx',
      email: !isSharedSession ? user?.email : 'xxxxxxxxxxx@xxxxx.xxx',
      linkedin: profileData?.profile?.linkedInData?.linkedIn_url?.[0]?.value || undefined,
      educationContent:
        educationContent.mainResponse?.resume_builder?.resume_builder || educationTemplate || '',
      workExperienceContent:
        workExperienceContent.mainResponse?.resume_builder?.resume_builder ||
        workExperienceTemplate ||
        '',
      leadershipContent:
        leadershipContent.mainResponse?.resume_builder?.resume_builder || leadershipTemplate || '',
      skillsContent:
        skillsContent.mainResponse?.resume_builder?.resume_builder || skillsTemplate || '',
    };
  };

  const tabs = useMemo(
    () => [
      {
        label: RESUME_BUILDER_STRINGS.education,
        value: 'education',
        sessionId: SOLUTION_SESSION_IDS.RESUME_BUILDER_EDUCATION,
      },
      {
        label: RESUME_BUILDER_STRINGS.workExperience,
        value: 'work_experience',
        sessionId: SOLUTION_SESSION_IDS.RESUME_BUILDER_WORK_EXPERIENCE,
      },
      {
        label: RESUME_BUILDER_STRINGS.leadership,
        value: 'leadership',
        sessionId: SOLUTION_SESSION_IDS.RESUME_BUILDER_LEADERSHIP,
      },
      {
        label: RESUME_BUILDER_STRINGS.skills,
        value: 'skills',
        sessionId: SOLUTION_SESSION_IDS.RESUME_BUILDER_SKILLS,
      },
    ],
    [],
  );

  const { data: template } = useGetResumeTemplateQuery(tabs[selectedTab].sessionId.split('.')[1]);

  const buildSubsessionUrl = useCallback(
    (subsession: string) => {
      if (isSharedSession && uuid) {
        return APP_ROUTE.SHARED_SOLUTION_SESSION.replace(':uuid', uuid).replace(
          ':sessionType',
          `resume_builder.${subsession}`,
        );
      }

      return `${APP_ROUTE.SOLUTION_SESSION.replace(':id?', 'resume_builder')}?subsession=${subsession}`;
    },
    [isSharedSession, uuid],
  );

  useEffect(() => {
    const subsession = isSharedSession
      ? activeSession?.split('.')[1] || null
      : new URLSearchParams(location.search).get('subsession');

    if (!subsession && activeSession?.startsWith('resume_builder')) {
      navigate(buildSubsessionUrl('education'), { replace: true });

      return;
    }

    if (subsession) {
      const tabIndex = tabs.findIndex((tab) => tab.value === subsession);

      if (tabIndex !== -1) {
        setSelectedTab(tabIndex);
      }
    }
  }, [location.search, tabs, activeSession, navigate, isSharedSession, buildSubsessionUrl]);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const handleTabChange = (tabIndex: number) => {
    setSelectedTab(tabIndex);
    const subsession = tabs[tabIndex]?.value;

    if (subsession) {
      navigate(buildSubsessionUrl(subsession), { replace: true });
    }
  };

  const handleCopyResumeSection = (content: string) => {
    const contentElement = document.querySelector('[data-resume-content-full]');

    if (!contentElement) {
      return;
    }

    copyResume(content, contentElement as HTMLElement);
  };

  const handleDownloadResumeSection = async (content: string) => {
    setIsDownloading(true);

    try {
      await downloadResume(content);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderContent = () => {
    return (
      <Box sx={contentContainerStyle}>
        <Box sx={mainContentStyle}>
          <Box sx={textContainerStyle}>
            {!isTablet && (
              <Box sx={headingContainerStyle}>
                <Typography variant="text-lg-semibold">{RESUME_BUILDER_STRINGS.heading}</Typography>
                <Button
                  id="resume-view-button"
                  hierarchy="Secondary gray"
                  size="sm"
                  onClick={() => setIsResumeModalOpen(true)}
                >
                  <AppIcon iconName="File06" diameter="16px" color="Gray/700" />
                  {RESUME_BUILDER_STRINGS.viewResume}
                </Button>
              </Box>
            )}
            <Box sx={tabsContainerStyle}>
              <HorizontalMenuTabs
                id="resume-builder-tabs"
                value={selectedTab}
                setValue={handleTabChange}
                tabs={tabs.map((tab) => tab.label)}
                variant="minimal-highlighted"
                numbered
              />
              {isTablet && (
                <Box sx={viewResumeButtonContainerStyle} onClick={() => setIsResumeModalOpen(true)}>
                  <AppIcon iconName="File06" diameter="20px" color="Gray/500" />
                </Box>
              )}
            </Box>
            <Box ref={scrollRef} sx={scrollBox}>
              <ResumeSection
                text={boxData.mainResponse?.resume_builder?.resume_builder || template || ''}
                solutionSession={tabs[selectedTab].sessionId}
                isloading={isLoadingResources}
              />
            </Box>
            <InfoModal
              open={isResumeModalOpen}
              onClose={() => setIsResumeModalOpen(false)}
              bodyContent={<Resume {...getResumeContent()} />}
              hideCircleImage
              featuredIcon={{
                iconName: 'File06',
                color: 'Colors/Base/Black',
                size: 'lg',
              }}
              headerContent={
                <Box sx={modalActionTabsContainerSx}>
                  <ActionTabs
                    actions={[
                      ...(betaMode
                        ? [
                            {
                              onClick: () =>
                                handleDownloadResumeSection(createFullResume(getResumeContent())),
                              icon: 'Download01' as const,
                              disabled: isDownloading,
                            },
                          ]
                        : []),
                      {
                        onClick: () =>
                          handleCopyResumeSection(createFullResume(getResumeContent())),
                        icon: 'Copy01' as const,
                        disabled: isDownloading,
                      },
                    ]}
                    borderColor="Colors/Base/Black"
                  />
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return renderContent();
};

const contentContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
};

const mainContentStyle: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'auto',
  flex: 1,
  display: 'flex',
  marginBottom: backrs.spacing['spacing-md'],
  flexDirection: 'column',
});

const scrollBox: SxProps<Theme> = ({ backrs }) => ({
  overflowY: 'scroll',
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  marginTop: backrs.spacing['spacing-xl'],
});

const textContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-md'],
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflowY: 'auto',
  position: 'relative',
  backgroundColor: {
    xs: backrs.colors['Colors/Base/White'],
    md: 'transparent',
  },
});

const headingContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: backrs.spacing['spacing-md'],
  marginTop: backrs.spacing['spacing-sm'],
});

const tabsContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  flexDirection: 'row',
  '& > *:first-of-type': {
    flex: 1,
  },
};

const viewResumeButtonContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: 'fit-content',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  cursor: 'pointer',
  py: {
    xs: backrs.spacing['spacing-none'],
    lg: backrs.spacing['spacing-lg'],
  },
});

const modalActionTabsContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};
